import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { StatusCode } from "api/protocols";
import { showSuccess, showError, enqueue, dequeue } from "redux/actions/ui";
import { IBooking } from "redux/reducers/models/teetime";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonNew } from "components/buttonNew";
import ReactDOM from "react-dom";
import Card from "components/card/Card";
import Popup from "components/popup/Popup";
import { Badge } from "components/badge/Badge";
import { useAppDispatch } from "hooks/redux";
import "./viewPortalBooking.scss";
import { useTranslation } from "react-i18next";
import Page from "components/page/Page";
import Divider from "components/divider";
import { GetBooking, PutCancelBooking, PutCancelBookingParticipant, PutConfirmBookingParticipant } from "api/rpc/2024-04/customer/booking";
import { useGuestPortalContext } from "../GuestPortalContext";
import { CustomIcon } from "components/customIcon/CustomIcon";
import BottomSheet from "components/bottomSheet/BottomSheet";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import classNames from "classnames";
import { convertHoles, convertTime } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";
interface IParams {
  id: string;
}

interface IState {
  selectedBooking: IBooking;
  formattedAddress: string;
  totalPlayers: number;
  endTime: string;
}

interface IEditBookingState {
  cancelPopUp: boolean;
  cancelSelfPopUp: boolean;
  hideEditButton: boolean;
  hideCancelButton: boolean;
  disableCancelButton: boolean;
  teeTimeNotInPast: boolean;
  isBooker: boolean;
  myParticipantId: number | null;
  isConfirmed: boolean;
}

export default function ViewPortalBooking() {
  const history = useHistory();
  const { t } = useTranslation();
  const { portalState } = useGuestPortalContext();
  const { id } = useParams<IParams>();
  const windowSize = useWindowSize();
  const [state, setState] = useState<IState>({
    selectedBooking: null,
    formattedAddress: "",
    totalPlayers: null,
    endTime: null,
  });

  const [editBookingState, setEditBookingState] = useState<IEditBookingState>({
    cancelPopUp: false,
    cancelSelfPopUp: false,
    hideEditButton: false,
    hideCancelButton: false,
    disableCancelButton: false,
    teeTimeNotInPast: false,
    isBooker: false,
    myParticipantId: null,
    isConfirmed: false,
  });

  const {
    state: cancelBookingState,
    updateModal: updateCancelBookingState,
    closeModal: closeCancelBookingState,
  } = useModal<{ selectedBooking: IBooking }>({ selectedBooking: null });

  const dispatch = useAppDispatch();

  // June 6, 2022
  const editDate = new Date("2022-06-06T00:00:00.000000Z").toUTCString();

  useEffect(() => {
    void getBooking();
  }, []);

  async function getBooking() {
    let [hideEditBooking, hideCancelBooking, disableCancelBooking, isBooker] = [true, true, true, false];

    const bookingRes = await GetBooking({ id: Number(id), group_bookings: true, extended: true }, true);

    if (bookingRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting booking")));
      return;
    }

    //Return to reservations page if booking isn't found
    if (bookingRes.data?.length === 0) {
      dispatch(showError(t("Booking not found")));
      history.push(`/tee-on/portal/bookings`);
    }

    if (bookingRes.data[0]?.status !== "cancelled") {
      [hideEditBooking, hideCancelBooking, disableCancelBooking, isBooker] = checkEditCancelTimes(bookingRes.data[0]);
      void checkTeeTimeInPast(bookingRes?.data[0]);
    }

    const fullAddress = `${bookingRes.data[0]?.tee_time?.facility?.address_line_1}, ${bookingRes.data[0]?.tee_time?.facility?.city},
      ${bookingRes.data[0]?.tee_time?.facility?.province_name}, ${bookingRes.data[0]?.tee_time?.facility?.postal}`;

    const booking = bookingRes.data[0];
    const myParticipant = booking?.participants?.find(
      participant => participant?.customer_id === portalState?.guest?.id && !participant?.cancelled_at,
    );
    const totalPlayers =
      booking?.grouped_bookings && booking?.grouped_bookings?.length > 0
        ? booking?.grouped_bookings?.reduce((quantity, booking) => quantity + booking?.quantity, 0) + booking?.quantity
        : booking?.quantity;

    const endTimeBooking =
      booking?.grouped_bookings && booking?.grouped_bookings?.length > 0
        ? booking?.grouped_bookings?.sort((a, b) => a?.tee_time?.start_time?.localeCompare(b?.tee_time?.start_time))[
            booking?.grouped_bookings?.length - 1
          ]
        : null;
    const endTime = endTimeBooking ? endTimeBooking?.tee_time?.start_time : null;

    ReactDOM.unstable_batchedUpdates(() => {
      setState(prevState => ({
        ...prevState,
        selectedBooking: bookingRes.data[0],
        formattedAddress: fullAddress,
        totalPlayers: totalPlayers,
        endTime: endTime,
      }));
      setEditBookingState(prevState => ({
        ...prevState,
        hideCancelButton: hideCancelBooking,
        hideEditButton: hideEditBooking,
        disableCancelButton: disableCancelBooking,
        isBooker: !!isBooker,
        myParticipantId: myParticipant?.id ?? null,
        isConfirmed: !!myParticipant?.reminder_confirmed_at,
      }));
    });
  }

  function checkTeeTimeInPast(booking: IBooking) {
    const bookingDate = booking?.tee_time?.date + " " + booking?.tee_time?.start_time + ":00";
    const currentTime = moment().utc();
    const bookingTime = moment(bookingDate)?.utc();
    const isParticipant = booking?.participants?.some(
      participant => participant?.customer_id === portalState?.guest?.id && !participant?.cancelled_at,
    );
    setEditBookingState(prevState => ({
      ...prevState,
      teeTimeNotInPast: currentTime.isBefore(bookingTime) && (!!isParticipant || booking?.customer_id === portalState?.guest?.id),
    }));
  }

  function checkEditCancelTimes(booking: IBooking) {
    let hideEditButton = false;
    let hideCancelButton = false;
    let disableCancelButton = false;
    const completedAtDate = new Date(booking?.completed_at).toUTCString();

    const currentDateUTCString = new Date().toUTCString();

    const teeTimeDateSetUp = new Date(booking?.tee_time?.date + "T" + booking?.tee_time?.start_time);
    //Set date-time based on facility timezone
    const teeTimeDateTimezoneString = new Date(teeTimeDateSetUp).toLocaleString("en-US", {
      timeZone: booking?.tee_time?.facility?.timezone,
    });
    const teeTimeCancelTime = new Date(teeTimeDateTimezoneString);
    const teeTimeEditTime = new Date(teeTimeDateTimezoneString);

    teeTimeCancelTime.setHours(teeTimeCancelTime.getHours() - booking?.tee_sheet_settings?.web_cancellation_hours);
    teeTimeEditTime.setHours(teeTimeEditTime.getHours() - booking?.tee_sheet_settings?.web_edit_hours);

    const teeTimeCancelTimeUTCString = teeTimeCancelTime.toUTCString();
    const teeTimeEditTimeUTCString = teeTimeEditTime.toUTCString();

    if (Date.parse(currentDateUTCString) >= Date.parse(teeTimeCancelTimeUTCString)) {
      hideCancelButton = true;
      disableCancelButton = true;
    }

    if (Date.parse(completedAtDate) <= Date.parse(editDate)) {
      hideEditButton = true;
    } else if (Date.parse(currentDateUTCString) >= Date.parse(teeTimeEditTimeUTCString)) {
      hideEditButton = true;
    }

    const isBooker = booking?.customer_id === portalState?.guest?.id;
    // Hide full cancel/edit for non-bookers; cancel-self remains available via separate button
    if (!isBooker) {
      hideCancelButton = true;
      hideEditButton = true;
    }

    return [hideEditButton, hideCancelButton, disableCancelButton, isBooker];
  }

  function initiateEditBooking() {
    if (state?.selectedBooking?.token) {
      history.push(`/tee-on/portal/bookings/${state?.selectedBooking?.id}/edit`);
    }
  }

  function initiateEditGroupBooking(booking: IBooking) {
    if (booking?.id) {
      history.push(`/tee-on/portal/bookings/${booking.id}/edit?returnTo=${id}`);
    }
  }

  function initiateCancelBooking() {
    setEditBookingState(prevState => ({ ...prevState, cancelPopUp: true }));
  }

  function initiateCancelSelf() {
    setEditBookingState(prevState => ({ ...prevState, cancelSelfPopUp: true }));
  }

  async function confirmBooking() {
    if (!editBookingState.myParticipantId) {
      dispatch(showError(t("Unable to confirm booking")));
      return;
    }
    const res = await PutConfirmBookingParticipant({ id: editBookingState.myParticipantId }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res.data : t("Error confirming booking")));
      return;
    }
    dispatch(showSuccess(t("Booking confirmed")));
    void getBooking();
  }

  async function cancelSelf() {
    if (!editBookingState.myParticipantId) {
      dispatch(showError(t("Unable to cancel yourself from this booking")));
      return;
    }
    const res = await PutCancelBookingParticipant({ id: editBookingState.myParticipantId }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res.data : t("Error cancelling your spot")));
      setEditBookingState(prevState => ({ ...prevState, cancelSelfPopUp: false }));
      return;
    }
    dispatch(showSuccess(t("You have been removed from the booking")));
    history.push(`/tee-on/portal/bookings`);
  }

  async function cancelBooking() {
    const groupedBookings = state?.selectedBooking?.grouped_bookings?.length > 0;
    if (groupedBookings) {
      //Grouped bookings
      const groupedBookingTokens = state?.selectedBooking?.grouped_bookings
        ?.filter(booking => booking?.status === "complete")
        ?.map(booking => booking?.token);
      const allBookingTokens = [state?.selectedBooking?.token, ...groupedBookingTokens];
      if (allBookingTokens?.length > 0) {
        dispatch(enqueue());
        const res = await Promise.all(allBookingTokens?.map(token => PutCancelBooking({ token }, false)));
        dispatch(dequeue());
        if (res?.some(res => res?.status !== StatusCode.OK)) {
          dispatch(showError(t("Error cancelling booking")));
        } else {
          dispatch(showSuccess(t("Successfully cancelled bookings")));
        }
      }
    } else {
      //Single booking
      const putCancelRes = await PutCancelBooking({ token: state.selectedBooking?.token }, true);
      if (putCancelRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error cancelling booking")));
        setEditBookingState(prevState => ({ ...prevState, cancelPopUp: false }));
        return;
      }
      dispatch(showSuccess(t("Successfully cancelled booking")));
    }

    history.push(`/tee-on/portal/bookings`);
  }

  async function cancelGroupBooking() {
    if (cancelBookingState?.selectedBooking) {
      const putCancelRes = await PutCancelBooking({ token: cancelBookingState?.selectedBooking?.token }, true);
      if (putCancelRes.status !== StatusCode.OK) {
        dispatch(showError(typeof putCancelRes?.data === "string" ? putCancelRes?.data : "Error cancelling booking"));
        return;
      }
      dispatch(showSuccess(t("Successfully cancelled booking")));
      void closeCancelBookingState();
      void getBooking();
    }
  }

  return (
    <Page
      title={t("Booking")}
      breadcrumbs={[{ prefix: true, label: t("Back to Bookings"), url: "/tee-on/portal/bookings" }]}
    >
      <div>
        {state.selectedBooking && (
          <div className="portal-reservations-view-booking">
            <div className="facility-container">
              {state.selectedBooking?.tee_time?.facility?.logo_source && (
                <img src={state.selectedBooking?.tee_time?.facility?.logo_source} alt={"Facility logo"} />
              )}
              <div className="facility-details-container">
                <h1 className="facility-name">
                  {state.selectedBooking?.tee_time?.facility?.long_name}{" "}
                  {state.selectedBooking?.tee_time?.course?.long_name !==
                  state.selectedBooking?.tee_time?.facility?.long_name
                    ? ` - ${state.selectedBooking?.tee_time?.course?.long_name}`
                    : ""}
                </h1>
                <p className="facility-address">{state.formattedAddress}</p>
              </div>
            </div>
            <Card title={t("Booking Details")}>
              <Card.Section>
                <div className="info-parent-container">
                  <div>
                    <p className="confirmation-text">
                      Booked By{" "}
                      {
                        <span className="confirmation-number">
                          {state.selectedBooking?.first_name} {state?.selectedBooking?.last_name}
                        </span>
                      }
                    </p>
                    <p className="confirmation-text">
                      Confirmation Number{" "}
                      {<span className="confirmation-number">{state.selectedBooking?.confirmation_number}</span>}
                    </p>
                  </div>
                  {state?.selectedBooking?.order_id && (
                    <Badge
                      type="success"
                      size="medium"
                      iconSize="medium"
                      iconLeft={<FontAwesomeIcon icon={["far", "dollar-sign"]} />}
                    >
                      {t("Pre Paid")}
                    </Badge>
                  )}
                  {state?.selectedBooking?.booking_fee_order_id && (
                    <Badge
                      type="success"
                      size="medium"
                      iconSize="medium"
                      iconLeft={<FontAwesomeIcon icon={["far", "dollar-sign"]} />}
                    >
                      {t("Booking Fee Paid")}
                    </Badge>
                  )}
                  {state?.selectedBooking?.status === "cancelled" && (
                    <Badge type="error" size="medium">
                      {t("Cancelled")}
                    </Badge>
                  )}
                </div>
                <div className="date-group-container">
                  <FontAwesomeIcon className="info-text" icon={["far", "calendar"]} />
                  <p className="date-time-info">
                    {moment(
                      `${state.selectedBooking?.tee_time?.date}T${state.selectedBooking?.tee_time?.start_time}`,
                    ).format("LLLL")}
                    {state?.endTime ? ` - ${convertTime(state?.endTime)}` : null}
                  </p>
                </div>

                <div className="info-parent-container ">
                  <div className="info-container">
                    <FontAwesomeIcon className="info-text" icon={["far", "golf-flag-hole"]} fixedWidth />
                    <p className="info-text">
                      {convertHoles(state.selectedBooking?.holes, state.selectedBooking?.tee_time?.course)} {t("Holes")}
                    </p>
                  </div>
                  <div className="info-container">
                    <FontAwesomeIcon className="info-text" icon={["far", "user-group"]} fixedWidth />
                    <p className="info-text">
                      {state.totalPlayers ?? state?.selectedBooking?.quantity}{" "}
                      {state.totalPlayers ?? state?.selectedBooking?.quantity > 1 ? t("Players") : t("Player")}
                    </p>
                  </div>
                  {state.selectedBooking?.power_cart_quantity >= 1 ? (
                    <div className="info-container">
                      <CustomIcon src="/public/images/powercart2-01.svg" size="small" />
                      <p className="info-text">
                        {state.selectedBooking?.power_cart_quantity}{" "}
                        {state.selectedBooking?.power_cart_quantity > 1 ? t("Power Carts") : t("Power Cart")}
                      </p>
                    </div>
                  ) : (
                    <div className="info-container">
                      <FontAwesomeIcon className="info-text" icon={["far", "person-walking"]} fixedWidth />
                      <p className="info-text">{t("Walking")}</p>
                    </div>
                  )}
                </div>
                {state?.selectedBooking?.grouped_bookings && state?.selectedBooking?.grouped_bookings?.length > 0 ? (
                  <div>
                    <br />
                    <p className="portal-reservations-view-booking-group-time">{t("Bookings in Group")}</p>
                    <br />
                    <div className="flex flex-column gap-2">
                      {state?.selectedBooking?.grouped_bookings?.map((booking, index) => {
                        return (
                          <div key={index} className="portal-reservations-view-booking-group-container">
                            <div className="flex items-center gap-2">
                              <p className="portal-reservations-view-booking-group-time">
                                {moment(booking?.tee_time?.start_time, "HH:mm")?.format("LT")}
                              </p>
                              {windowSize.width > MOBILE_WIDTH ? (
                                <div className="info-parent-container ">
                                  <div className="info-container">
                                    <FontAwesomeIcon
                                      className="info-text"
                                      icon={["far", "golf-flag-hole"]}
                                      fixedWidth
                                    />
                                    <p className="info-text">
                                      {convertHoles(booking?.holes, booking?.tee_time?.course)} {t("Holes")}
                                    </p>
                                  </div>
                                  <div className="info-container">
                                    <FontAwesomeIcon className="info-text" icon={["far", "user-group"]} fixedWidth />
                                    <p className="info-text">
                                      {booking?.quantity} {booking?.quantity > 1 ? t("Players") : t("Player")}
                                    </p>
                                  </div>
                                  {booking?.power_cart_quantity >= 1 ? (
                                    <div className="info-container">
                                      <CustomIcon src="/public/images/powercart2-01.svg" size="small" />
                                      <p className="info-text">
                                        {booking?.power_cart_quantity}{" "}
                                        {booking?.power_cart_quantity > 1 ? t("Power Carts") : t("Power Cart")}
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="info-container">
                                      <FontAwesomeIcon
                                        className="info-text"
                                        icon={["far", "person-walking"]}
                                        fixedWidth
                                      />
                                      <p className="info-text">{t("Walking")}</p>
                                    </div>
                                  )}
                                  {booking?.status === "cancelled" ? (
                                    <Badge type="error" size="medium">
                                      {t("Cancelled")}
                                    </Badge>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                            <div className="flex flex-wrap justify-end gap-2">
                              {editBookingState?.teeTimeNotInPast && booking?.status !== "cancelled" ? (
                                <ButtonNew
                                  type="secondary"
                                  size="small"
                                  onClick={() => updateCancelBookingState({ selectedBooking: booking, isOpen: true })}
                                  disabled={editBookingState.disableCancelButton}
                                >
                                  {t("Cancel Booking")}
                                </ButtonNew>
                              ) : null}

                              <ButtonNew type="primary" size="small"
                               onClick={() => initiateEditGroupBooking(booking)}
                               >
                                {t("Edit Booking")}
                              </ButtonNew>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </Card.Section>
            </Card>
            <Divider />
            <div
              className={classNames("bottom-buttons-container", {
                cancel_disabled: editBookingState.disableCancelButton,
              })}
            >
              {editBookingState?.teeTimeNotInPast && editBookingState.myParticipantId && !editBookingState.isConfirmed ? (
                <div className="button-container">
                  <ButtonNew className="view-booking-button" type="primary" size="medium" onClick={confirmBooking}>
                    {t("Confirm Booking")}
                  </ButtonNew>
                </div>
              ) : null}

              {editBookingState?.teeTimeNotInPast && editBookingState.myParticipantId && !editBookingState.disableCancelButton ? (
                <div className="button-container">
                  <ButtonNew
                    className="view-booking-button"
                    type="secondary"
                    size="medium"
                    onClick={initiateCancelSelf}
                    disabled={editBookingState.disableCancelButton}
                  >
                    {t("Cancel Myself")}
                  </ButtonNew>
                </div>
              ) : null}

              {editBookingState?.teeTimeNotInPast && editBookingState.isBooker && !editBookingState.hideCancelButton ? (
                <div className="button-container">
                  <ButtonNew
                    className="view-booking-button"
                    type="secondary"
                    size="medium"
                    onClick={initiateCancelBooking}
                    disabled={editBookingState.disableCancelButton}
                    helpText={t("Current date / time is past cancel window")}
                  >
                    {state?.selectedBooking?.grouped_bookings && state?.selectedBooking?.grouped_bookings?.length > 0
                      ? t("Cancel all bookings")
                      : t("Cancel Entire Booking")}
                  </ButtonNew>
                </div>
              ) : null}

              {editBookingState?.teeTimeNotInPast && editBookingState.isBooker ? (
                <ButtonNew className="view-booking-button" type="primary" size="medium" onClick={initiateEditBooking}>
                  {!editBookingState.hideEditButton ? t("Edit Booking") : t("Edit Players")}
                </ButtonNew>
              ) : null}
            </div>
          </div>
        )}

        <Popup
          onCancel={() => setEditBookingState(prevState => ({ ...prevState, cancelPopUp: false }))}
          onOk={cancelBooking}
          cancelText={t("Close")}
          okText={t("Cancel Booking")}
          open={editBookingState.cancelPopUp && windowSize.width > MOBILE_WIDTH}
          type="warning"
          title={t("Confirm Cancellation")}
          description={
            state?.selectedBooking?.grouped_bookings?.length > 0
              ? t("Are you sure you want to cancel this booking and all bookings within this group?")
              : t("Are you sure you want to cancel this booking?")
          }
        />

        <Popup
          onCancel={() => setEditBookingState(prevState => ({ ...prevState, cancelSelfPopUp: false }))}
          onOk={cancelSelf}
          cancelText={t("Close")}
          okText={t("Cancel Myself")}
          open={editBookingState.cancelSelfPopUp && windowSize.width > MOBILE_WIDTH}
          type="warning"
          title={t("Cancel Yourself")}
          description={t("Are you sure you want to remove yourself from this booking?")}
        />

        <Popup
          onCancel={closeCancelBookingState}
          onOk={cancelGroupBooking}
          cancelText={t("Close")}
          okText={t("Cancel Booking")}
          open={cancelBookingState?.isOpen}
          type="warning"
          title={t("Confirm Cancellation")}
          description={t("Are you sure you want to cancel the booking at {{bookingDate}}?", {
            bookingDate: moment(cancelBookingState?.selectedBooking?.tee_time?.start_time, "HH:mm")?.format("LT"),
          })}
        />

        <BottomSheet
          open={editBookingState.cancelPopUp && windowSize.width <= MOBILE_WIDTH}
          onClose={() => setEditBookingState(prevState => ({ ...prevState, cancelPopUp: false }))}
          type="warning"
          content={
            state?.selectedBooking?.grouped_bookings?.length > 0
              ? t("Are you sure you want to cancel this booking and all bookings within this group?")
              : t("Are you sure you want to cancel this booking?")
          }
          primaryAction={{ label: t("Cancel Booking"), onClick: cancelBooking }}
        />

        <BottomSheet
          open={editBookingState.cancelSelfPopUp && windowSize.width <= MOBILE_WIDTH}
          onClose={() => setEditBookingState(prevState => ({ ...prevState, cancelSelfPopUp: false }))}
          type="warning"
          content={t("Are you sure you want to remove yourself from this booking?")}
          primaryAction={{ label: t("Cancel Myself"), onClick: cancelSelf }}
        />
      </div>
    </Page>
  );
}
